import { useState, useEffect, useCallback } from "react";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  CheckCircle, Spinner, ArrowRight, Buildings, LinkSimple, PlusCircle,
  SkipForward, MapPin, Warning, ClockCounterClockwise, Sparkle, MagnifyingGlass
} from "@phosphor-icons/react";
import { nmcListBatches, nmcGetBatch, nmcUpdateAction, nmcApplyMatches, nmcRematchBatch, nmcLlmVerify, nmcRescanNew, nmcLlmMatchNew, feesApplyMatches } from "../lib/api";

export default function NmcReview() {
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [llmVerifying, setLlmVerifying] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [llmMatching, setLlmMatching] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState("");
  const [savingAction, setSavingAction] = useState(null); // sno being saved

  useEffect(() => {
    loadBatches();
  }, []);

  const loadBatches = async () => {
    setLoading(true);
    try {
      const res = await nmcListBatches();
      const items = res.data.batches || [];
      setBatches(items);
      // Auto-select most recent pending or matching batch
      const active = items.find(b => b.status === "pending" || b.status === "matching");
      if (active) {
        setSelectedBatchId(active.batch_id);
      }
    } catch (e) {
      setError("Failed to load batches");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (selectedBatchId) loadBatch(selectedBatchId);
  }, [selectedBatchId]); // batch load on selection change

  const loadBatch = async (bid) => {
    setLoading(true);
    setBatchData(null);
    setApplyResult(null);
    try {
      const res = await nmcGetBatch(bid);
      setBatchData(res.data);
      if (res.data.status === "applied") {
        setApplyResult(res.data.apply_result);
      }
      // Resume polling if LLM operation is running
      if (res.data.llm_status === "running") {
        setLlmVerifying(true);
        pollLlmProgress();
      }
      // Poll if matching is in progress (fees CSV background task)
      if (res.data.status === "matching") {
        pollMatching(bid);
      }
    } catch (e) {
      setError("Failed to load batch data");
    }
    setLoading(false);
  };

  const pollMatching = (bid) => {
    const interval = setInterval(async () => {
      try {
        const res = await nmcGetBatch(bid);
        setBatchData(res.data);
        if (res.data.status !== "matching") {
          clearInterval(interval);
        }
      } catch (e) {
        clearInterval(interval);
      }
    }, 5000);
  };


  const handleAction = useCallback(async (listName, idx, action, mergeCode) => {
    if (!batchData || !selectedBatchId) return;
    const entry = batchData.matches[listName][idx];
    if (!entry) return;

    setSavingAction(entry.sno);
    try {
      await nmcUpdateAction(selectedBatchId, {
        sno: entry.sno,
        action,
        merge_code: mergeCode || undefined,
      });
      // Update local state
      setBatchData(prev => {
        const updated = { ...prev, matches: { ...prev.matches } };
        const arr = [...updated.matches[listName]];
        arr[idx] = { ...arr[idx], action };
        if (mergeCode) arr[idx].matched_code = mergeCode;
        updated.matches[listName] = arr;
        return updated;
      });
    } catch (e) {
      console.error("Failed to save action", e);
    }
    setSavingAction(null);
  }, [batchData, selectedBatchId]);

  const handleApply = async () => {
    if (!selectedBatchId) return;
    setApplying(true);
    setError("");
    try {
      const applyFn = batchData?.batch_type === "fees_csv" ? feesApplyMatches : nmcApplyMatches;
      const res = await applyFn({ batch_id: selectedBatchId });
      setApplyResult(res.data);
      loadBatches();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Apply failed");
    }
    setApplying(false);
  };

  const handleRematch = async () => {
    if (!selectedBatchId) return;
    setRematching(true);
    setError("");
    try {
      const res = await nmcRematchBatch(selectedBatchId);
      // Reload the batch with fresh matches
      await loadBatch(selectedBatchId);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Rematch failed");
    }
    setRematching(false);
  };

  const handleLlmVerify = async () => {
    if (!selectedBatchId) return;
    setLlmVerifying(true);
    setError("");
    try {
      await nmcLlmVerify(selectedBatchId);
      // Start polling for progress
      pollLlmProgress();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "LLM verification failed");
      setLlmVerifying(false);
    }
  };

  const pollLlmProgress = () => {
    const interval = setInterval(async () => {
      try {
        const res = await nmcGetBatch(selectedBatchId);
        const progress = res.data.llm_progress;
        const status = res.data.llm_status;
        setBatchData(res.data);

        if (status === "done" || status === "error") {
          clearInterval(interval);
          setLlmVerifying(false);
          setLlmMatching(false);
          if (status === "done" && progress) {
            const stateInfo = progress.states_done ? ` across ${progress.states_total} states` : '';
            setApplyResult({ promoted: progress.promoted, total: progress.total, message: `LLM processed ${progress.total} entries${stateInfo} — ${progress.promoted} matched` });
          }
        }
      } catch (e) {
        clearInterval(interval);
        setLlmVerifying(false);
        setLlmMatching(false);
      }
    }, 3000);
  };

  const handleRescanNew = async () => {
    if (!selectedBatchId) return;
    setRescanning(true);
    setError("");
    try {
      const res = await nmcRescanNew(selectedBatchId);
      setApplyResult({ message: `Rescanned ${res.data.total} NEW entries → ${res.data.promoted} moved to REVIEW` });
      await loadBatch(selectedBatchId);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Rescan failed");
    }
    setRescanning(false);
  };

  const handleLlmMatchNew = async () => {
    if (!selectedBatchId) return;
    setLlmMatching(true);
    setError("");
    try {
      await nmcLlmMatchNew(selectedBatchId);
      pollLlmProgress();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "LLM match failed");
      setLlmMatching(false);
    }
  };

  const counts = batchData?.matches ? {
    auto: batchData.matches.auto_matched?.length || 0,
    review: batchData.matches.review_needed?.length || 0,
    unmatched: batchData.matches.unmatched?.length || 0,
    p2_in_auto: (batchData.matches.auto_matched || []).filter(e => e.match_pass === "fs_promoted").length,
  } : { auto: 0, review: 0, unmatched: 0, p2_in_auto: 0 };
  const llmVerifyCount = counts.review + counts.p2_in_auto;

  // Count how many review entries have no action set (NEW auto-creates, don't count)
  const pendingReview = (batchData?.matches?.review_needed || []).filter(e => !e.action).length;
  const isApplied = batchData?.status === "applied";
  const isMcc = batchData?.batch_type === "mcc_collapse";

  return (
    <div data-testid="nmc-review">
      {/* Batch selector */}
      <div className="border border-zinc-200 border-t-0 p-4 bg-[#F4F4F5] flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Buildings size={16} weight="bold" className="text-[#6043EF]" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">NMC REVIEW BATCHES</span>
        </div>
        <div className="flex items-center gap-3">
          {batches.length > 0 && (
            <Select value={selectedBatchId || ""} onValueChange={setSelectedBatchId}>
              <SelectTrigger className="w-80 border-zinc-300 bg-white text-xs" data-testid="nmc-batch-select">
                <SelectValue placeholder="Select a batch..." />
              </SelectTrigger>
              <SelectContent>
                {batches.map(b => (
                  <SelectItem key={b.batch_id} value={b.batch_id}>
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[8px] ${b.batch_type === "fees_csv" ? "border-[#00C04B] text-[#00C04B]" : b.batch_type === "mcc_collapse" ? "border-[#FF2A00] text-[#FF2A00]" : "border-[#6043EF] text-[#6043EF]"}`}>
                        {b.batch_type === "fees_csv" ? "FEES" : b.batch_type === "mcc_collapse" ? "MCC" : "NMC"}
                      </Badge>
                      {b.batch_type === "mcc_collapse" ? (
                        <span className="font-['IBM_Plex_Mono'] text-[10px]">{b.label || "MCC collapse"}</span>
                      ) : (
                        <span className="font-['IBM_Plex_Mono'] text-[10px]">
                          {b.parsed_meta?.course || ""} {b.parsed_meta?.current_year ? `AY${b.parsed_meta.current_year}` : ""}
                        </span>
                      )}
                      <span className="text-[10px] text-zinc-400">
                        {b.parsed_meta?.total} colleges
                      </span>
                      <Badge variant="outline" className={`text-[9px] ${b.status === "applied" ? "border-[#00C04B] text-[#00C04B]" : "border-[#FFC107] text-[#92700C]"}`}>
                        {b.status}
                      </Badge>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <button
            onClick={loadBatches}
            className="h-8 px-3 border border-zinc-300 text-[#52525B] text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-50 flex items-center gap-1"
            data-testid="nmc-refresh-btn"
          >
            <ClockCounterClockwise size={12} /> REFRESH
          </button>
          {selectedBatchId && batchData?.status !== "applied" && (
            <button
              onClick={handleRematch}
              disabled={rematching}
              className="h-8 px-3 bg-[#6043EF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] disabled:opacity-50 flex items-center gap-1"
              data-testid="nmc-rematch-btn"
            >
              {rematching ? <Spinner size={12} className="animate-spin" /> : <ArrowRight size={12} weight="bold" />}
              {rematching ? "REMATCHING..." : "REMATCH"}
            </button>
          )}
          {selectedBatchId && batchData?.status !== "applied" && llmVerifyCount > 0 && (
            <button
              onClick={handleLlmVerify}
              disabled={llmVerifying}
              className="h-8 px-3 bg-[#09090B] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1"
              data-testid="nmc-llm-verify-btn"
            >
              {llmVerifying ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="bold" />}
              {llmVerifying ? "VERIFYING..." : `LLM VERIFY (${llmVerifyCount})`}
            </button>
          )}
          {selectedBatchId && batchData?.status !== "applied" && counts.unmatched > 0 && (
            <button
              onClick={handleRescanNew}
              disabled={rescanning}
              className="h-8 px-3 border border-[#09090B] text-[#09090B] text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-100 disabled:opacity-50 flex items-center gap-1"
              data-testid="nmc-rescan-new-btn"
            >
              {rescanning ? <Spinner size={12} className="animate-spin" /> : <MagnifyingGlass size={12} weight="bold" />}
              {rescanning ? "SCANNING..." : `RESCAN NEW (${counts.unmatched})`}
            </button>
          )}
          {selectedBatchId && batchData?.status !== "applied" && counts.unmatched > 0 && (
            <button
              onClick={handleLlmMatchNew}
              disabled={llmMatching}
              className="h-8 px-3 bg-[#09090B] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-zinc-800 disabled:opacity-50 flex items-center gap-1"
              data-testid="nmc-llm-match-new-btn"
            >
              {llmMatching ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="bold" />}
              {llmMatching ? "MATCHING..." : `LLM MATCH NEW (${counts.unmatched})`}
            </button>
          )}
        </div>
      </div>

      {/* LLM Progress Bar */}
      {(llmVerifying || llmMatching) && batchData?.llm_progress && (
        <div className="border border-zinc-200 border-t-0 p-4 bg-[#09090B]/5 animate-fade-in" data-testid="llm-progress">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkle size={14} weight="bold" className="text-[#09090B]" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B]">
                {llmMatching ? "LLM MATCHING NEW ENTRIES" : "LLM VERIFICATION IN PROGRESS"}
              </span>
              {batchData.llm_progress.current_state && (
                <span className="font-['IBM_Plex_Mono'] text-[10px] text-[#6043EF]">{batchData.llm_progress.current_state}</span>
              )}
            </div>
            <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]">
              {batchData.llm_progress.completed}/{batchData.llm_progress.total} checked — {batchData.llm_progress.promoted} matched
              {batchData.llm_progress.states_done != null && (
                <span className="ml-2">({batchData.llm_progress.states_done}/{batchData.llm_progress.states_total} states)</span>
              )}
              {batchData.llm_progress.demoted > 0 && <span className="text-[#FF2A00] ml-1">— {batchData.llm_progress.demoted} to NEW</span>}
              {batchData.llm_progress.errors > 0 && <span className="text-[#FF2A00] ml-2">{batchData.llm_progress.errors} errors</span>}
            </span>
          </div>
          <div className="w-full h-2 bg-zinc-200">
            <div
              className="h-full bg-[#09090B] transition-all duration-500 ease-out"
              style={{ width: `${batchData.llm_progress.total > 0 ? (batchData.llm_progress.completed / batchData.llm_progress.total * 100) : 0}%` }}
            />
          </div>
          {batchData.llm_progress.call_log && batchData.llm_progress.call_log.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {batchData.llm_progress.call_log.map((c, i) => (
                <span key={i} className={`inline-flex items-center gap-1 text-[9px] font-['IBM_Plex_Mono'] border px-1.5 py-0.5 ${
                  c.skipped ? "border-zinc-200 text-zinc-400" : c.matched > 0 ? "border-[#00C04B] text-[#00C04B]" : "border-zinc-300 text-[#52525B]"
                }`}>
                  {c.state?.substring(0, 12)} {c.skipped ? "skip" : `${c.matched}/${c.left}`}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="border border-zinc-200 border-t-0 p-8 text-center">
          <Spinner size={20} className="animate-spin text-[#6043EF] mx-auto" />
        </div>
      )}

      {/* No batches */}
      {!loading && batches.length === 0 && (
        <div className="border border-zinc-200 border-t-0 p-8 text-center">
          <Buildings size={32} className="text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-[#52525B] font-sans">
            No NMC review batches found. Upload an NMC seat matrix PDF in the <strong>MANAGE COLLEGES</strong> tab.
          </p>
        </div>
      )}

      {/* Matching in progress (fees CSV background task) */}
      {batchData?.status === "matching" && !loading && (
        <div className="border border-zinc-200 border-t-0 p-6 text-center animate-fade-in" data-testid="matching-progress">
          <Spinner size={24} className="animate-spin text-[#00C04B] mx-auto mb-3" />
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#09090B] mb-1">MATCHING IN PROGRESS</p>
          <p className="text-sm text-[#52525B] font-sans">
            Matching {batchData.parsed_meta?.total || 0} colleges against database. This may take a minute...
          </p>
        </div>
      )}

      {/* Applied result */}
      {applyResult && (
        <div className="border border-zinc-200 border-t-0 p-4 bg-[#00C04B]/5 animate-fade-in" data-testid="nmc-apply-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={16} weight="bold" className="text-[#00C04B]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#00C04B]">APPLIED SUCCESSFULLY</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm"><strong className="font-['IBM_Plex_Mono']">{applyResult.updated}</strong> updated</span>
            <span className="text-sm"><strong className="font-['IBM_Plex_Mono']">{applyResult.new}</strong> new created</span>
            <span className="text-sm"><strong className="font-['IBM_Plex_Mono']">{applyResult.total}</strong> total</span>
          </div>
        </div>
      )}

      {/* Batch data */}
      {batchData && !loading && (
        <div className="border border-zinc-200 border-t-0" data-testid="nmc-review-data">
          {/* Summary bar */}
          <div className="p-3 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <span className="font-['IBM_Plex_Mono'] text-xs font-bold text-[#09090B]">
                {batchData.parsed_meta?.total} {batchData.batch_type === "mcc_collapse" ? "MCC institutes" : "colleges"}
              </span>
              {batchData.batch_type === "mcc_collapse" ? (
                <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]">{batchData.label || "MCC college-code collapse review"}</span>
              ) : (
                <span className="font-['IBM_Plex_Mono'] text-xs text-[#52525B]">
                  {batchData.parsed_meta?.course} — AY {batchData.parsed_meta?.current_year}
                </span>
              )}
              {batchData.parsed_meta?.pub_date && (
                <span className="font-['IBM_Plex_Mono'] text-[10px] text-zinc-400">as of {batchData.parsed_meta.pub_date}</span>
              )}
              <Badge className="bg-[#00C04B]/10 text-[#00C04B] border-[#00C04B] text-[10px]">{counts.auto} AUTO</Badge>
              <Badge className="bg-[#FFC107]/10 text-[#92700C] border-[#FFC107] text-[10px]">{counts.review} REVIEW</Badge>
              <Badge className="bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00] text-[10px]">{counts.unmatched} NEW</Badge>
            </div>
            {!isApplied && (
              <div className="flex items-center gap-2">
                {pendingReview > 0 && (
                  <span className="flex items-center gap-1 text-[10px] text-[#92700C]">
                    <Warning size={12} /> {pendingReview} pending decisions
                  </span>
                )}
                <button
                  onClick={handleApply}
                  disabled={applying}
                  className="h-8 px-4 bg-[#6043EF] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#4A28C2] disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="nmc-apply-btn"
                >
                  {applying ? <Spinner size={12} className="animate-spin" /> : <ArrowRight size={12} weight="bold" />}
                  {applying ? "APPLYING..." : "APPLY ALL"}
                </button>
              </div>
            )}
            {isApplied && (
              <Badge className="bg-[#00C04B]/10 text-[#00C04B] border-[#00C04B] text-[10px]">APPLIED</Badge>
            )}
          </div>

          {/* Tabs */}
          <Tabs defaultValue={counts.review > 0 ? "review" : counts.unmatched > 0 ? "new" : "auto"} className="w-full">
            <TabsList className="w-full justify-start bg-[#F4F4F5] border-b border-zinc-200 rounded-none h-10 p-0">
              <TabsTrigger value="auto" className="rounded-none text-xs font-bold uppercase tracking-[0.15em] data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#00C04B] h-full px-6" data-testid="nmc-tab-auto">
                AUTO ({counts.auto})
              </TabsTrigger>
              <TabsTrigger value="review" className="rounded-none text-xs font-bold uppercase tracking-[0.15em] data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#FFC107] h-full px-6" data-testid="nmc-tab-review">
                REVIEW ({counts.review})
              </TabsTrigger>
              <TabsTrigger value="new" className="rounded-none text-xs font-bold uppercase tracking-[0.15em] data-[state=active]:bg-white data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#FF2A00] h-full px-6" data-testid="nmc-tab-new">
                NEW ({counts.unmatched})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="auto" className="mt-0">
              <MatchTable
                entries={batchData.matches.auto_matched}
                type="auto"
                batchType={batchData.batch_type}
                onAction={isApplied ? null : (idx, action, code) => handleAction("auto_matched", idx, action, code)}
                savingAction={savingAction}
              />
            </TabsContent>
            <TabsContent value="review" className="mt-0">
              <MatchTable
                entries={batchData.matches.review_needed}
                type="review"
                batchType={batchData.batch_type}
                onAction={isApplied ? null : (idx, action, code) => handleAction("review_needed", idx, action, code)}
                savingAction={savingAction}
              />
            </TabsContent>
            <TabsContent value="new" className="mt-0">
              <MatchTable
                entries={batchData.matches.unmatched}
                type="unmatched"
                batchType={batchData.batch_type}
                savingAction={savingAction}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}

      {error && (
        <div className="border border-zinc-200 border-t-0 p-3 border-[#FF2A00] bg-[#FF2A00]/5 text-sm text-[#FF2A00]">{error}</div>
      )}
    </div>
  );
}


function MatchTable({ entries, type, onAction, savingAction, onMarkAllNew, batchType }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-[#52525B]">No entries</div>
    );
  }

  const isFees = batchType === "fees_csv";
  const isMcc = batchType === "mcc_collapse";
  const allMarkedNew = type === "unmatched" && entries.every(e => e.action === "create");

  const fmtFee = (val, cur) => {
    if (!val && val !== 0) return "—";
    if (cur === "USD") return `$${val.toLocaleString()}`;
    if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
    return `${val.toLocaleString()}`;
  };

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-[#F4F4F5] hover:bg-[#F4F4F5]">
            <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] w-8">#</TableHead>
            {!isFees && <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">STATE</TableHead>}
            <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] min-w-[220px]">
              {isFees ? "COLLEGE" : isMcc ? "MCC INSTITUTE (raw)" : "NMC NAME"}
            </TableHead>
            {!isFees && !isMcc && <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">MGMT</TableHead>}
            {!isFees && !isMcc && <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">SEATS</TableHead>}
            {isMcc && <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">ROWS</TableHead>}
            {isMcc && <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] min-w-[200px]">MCC PARSER (current)</TableHead>}
            {isFees && (
              <>
                <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">GOVT</TableHead>
                <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">REGULAR</TableHead>
                <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">NRI</TableHead>
                <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">INTAKE</TableHead>
                <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">EXTRA</TableHead>
              </>
            )}
            {type !== "unmatched" && (
              <>
                <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] min-w-[220px]">MATCHED TO</TableHead>
                <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B] text-right">SCORE</TableHead>
              </>
            )}
            {onAction && (
              <TableHead className="font-bold text-[10px] uppercase tracking-[0.15em] text-[#09090B]">
                <span className="flex items-center gap-2">
                  ACTION
                  {onMarkAllNew && (
                    <button
                      onClick={onMarkAllNew}
                      disabled={allMarkedNew}
                      className={`h-5 px-2 text-[8px] font-bold uppercase tracking-wider flex items-center gap-1 transition-colors ${
                        allMarkedNew ? "bg-[#6043EF] text-white" : "border border-zinc-400 text-[#52525B] hover:bg-zinc-100"
                      }`}
                      data-testid="nmc-mark-all-new"
                    >
                      {allMarkedNew ? <CheckCircle size={8} /> : null}
                      ALL NEW
                    </button>
                  )}
                </span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((e, i) => {
            const isSaving = savingAction === e.sno;
            const currentAction = e.action;
            return (
              <TableRow
                key={e.sno}
                className={`hover:bg-zinc-50 transition-colors duration-150`}
                data-testid={`nmc-row-${type}-${i}`}
              >
                <TableCell className="font-['IBM_Plex_Mono'] text-xs text-zinc-400">{e.sno}</TableCell>
                {!isFees && <TableCell className="text-xs text-[#52525B]">{e.state}</TableCell>}
                <TableCell className="text-xs">
                  <span className="font-medium">{e.name}</span>
                  {e.city && (
                    <span className="flex items-center gap-1 text-[10px] text-zinc-400 mt-0.5">
                      <MapPin size={9} /> {e.city}
                    </span>
                  )}
                  {isFees && e.year_raw && (
                    <span className="text-[10px] text-zinc-400 ml-1">({e.year_raw})</span>
                  )}
                </TableCell>
                {!isFees && !isMcc && (
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] uppercase">{e.management_raw}</Badge>
                  </TableCell>
                )}
                {!isFees && !isMcc && <TableCell className="text-right font-['IBM_Plex_Mono'] text-sm font-bold">{e.seats_current_year}</TableCell>}
                {isMcc && <TableCell className="text-right font-['IBM_Plex_Mono'] text-xs text-[#52525B]">{e.rows}</TableCell>}
                {isMcc && (
                  <TableCell className="text-xs">
                    {e.current_name ? (
                      <div>
                        <span className={e.same_college === true ? "text-[#00913D]" : "text-[#FF2A00]"}>{e.current_name}</span>
                        <span className="block text-[10px] text-zinc-400 font-['IBM_Plex_Mono']">{e.current_code}{e.current_city ? ` · ${e.current_city}` : ""}</span>
                      </div>
                    ) : (
                      <span className="text-[10px] text-zinc-400 italic">{e.current_code ? e.current_code : "parser left unmapped"}</span>
                    )}
                  </TableCell>
                )}
                {isFees && (
                  <>
                    <TableCell className="text-right font-['IBM_Plex_Mono'] text-xs font-bold text-[#09090B]">
                      {fmtFee(e.govt_fees, e.govt_currency)}
                    </TableCell>
                    <TableCell className="text-right font-['IBM_Plex_Mono'] text-xs text-[#52525B]">
                      {fmtFee(e.regular_fees, e.regular_currency)}
                    </TableCell>
                    <TableCell className="text-right font-['IBM_Plex_Mono'] text-xs text-[#52525B]">
                      {fmtFee(e.nri_fees, e.nri_currency)}
                    </TableCell>
                    <TableCell className="text-right font-['IBM_Plex_Mono'] text-xs">{e.intake || "—"}</TableCell>
                    <TableCell className="text-xs text-[#52525B]">
                      {e.full_course_fees && <Badge className="bg-[#FFC107]/20 text-[#92700C] text-[8px]">FULL COURSE</Badge>}
                      {e.donation?.hostel && <span className="block text-[10px]">Hstl: {e.donation.hostel}</span>}
                      {e.donation?.security_deposit && <span className="block text-[10px]">SD: {e.donation.security_deposit}</span>}
                      {e.donation?.misc && <span className="block text-[10px]">Misc: {e.donation.misc}</span>}
                    </TableCell>
                  </>
                )}
                {type !== "unmatched" && (
                  <>
                    <TableCell className="text-xs">
                      {e.matched_name && (
                        <div>
                          <span className="font-medium">{e.matched_name}</span>
                          <span className="block text-[10px] text-zinc-400 font-['IBM_Plex_Mono']">{e.matched_code} — {e.matched_body}</span>
                          {e.matched_city && (
                            <span className="flex items-center gap-1 text-[10px] text-[#6043EF] mt-0.5">
                              <MapPin size={9} /> {e.matched_city}
                            </span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-['IBM_Plex_Mono'] text-xs font-bold ${e.match_score >= 85 ? "text-[#00C04B]" : e.match_score >= 50 ? "text-[#92700C]" : "text-[#FF2A00]"}`}>
                        {e.match_score}%
                      </span>
                      {e.match_pass === "fs_promoted" && (
                        <span className="block text-[9px] font-bold text-[#6043EF] mt-0.5">P2</span>
                      )}
                      {e.match_pass === "llm_verified" && (
                        <span className="block text-[9px] font-bold text-[#09090B] mt-0.5">LLM</span>
                      )}
                      {e.match_pass === "llm_rejected" && (
                        <span className="block text-[9px] font-bold text-[#FF2A00] mt-0.5">LLM:DIFF</span>
                      )}
                      {e.match_pass === "rescan" && (
                        <span className="block text-[9px] font-bold text-[#52525B] mt-0.5">RESCAN</span>
                      )}
                      {e.match_pass === "llm_matched" && (
                        <span className="block text-[9px] font-bold text-[#09090B] mt-0.5">LLM:MATCH</span>
                      )}
                    </TableCell>
                  </>
                )}
                {onAction && (
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {isSaving && <Spinner size={10} className="animate-spin text-[#6043EF]" />}
                      {type !== "unmatched" && e.matched_code && (
                        <button
                          onClick={() => onAction(i, "merge", e.matched_code)}
                          disabled={isSaving}
                          className={`h-6 px-2 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 transition-colors ${
                            currentAction === "merge" ? "bg-[#00C04B] text-white" : "border border-zinc-300 text-[#52525B] hover:bg-zinc-50"
                          }`}
                          data-testid={`nmc-merge-${type}-${i}`}
                        >
                          <LinkSimple size={10} /> MERGE
                        </button>
                      )}
                      <button
                        onClick={() => onAction(i, "create")}
                        disabled={isSaving}
                        className={`h-6 px-2 text-[9px] font-bold uppercase tracking-wider flex items-center gap-1 transition-colors ${
                          currentAction === "create" ? "bg-[#6043EF] text-white" : "border border-zinc-300 text-[#52525B] hover:bg-zinc-50"
                        }`}
                        data-testid={`nmc-create-${type}-${i}`}
                      >
                        <PlusCircle size={10} /> NEW
                      </button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
